import { useState, useRef, useEffect } from 'react';

interface UseVideoPlayerReturn {
  // State
  videoEnded: boolean;
  videoError: boolean;
  videoLoaded: boolean;
  showContent: boolean;
  
  // Refs
  videoRef: React.RefObject<HTMLVideoElement>;
  loopVideoRef: React.RefObject<HTMLVideoElement>;
  
  // Methods
  handleOpenInvitation: () => Promise<void>;
  
  // Setters for external control
  setShowQuickActions: (show: boolean) => void;
  setIsPlaying: (playing: boolean) => void;
}

interface UseVideoPlayerProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  onShowQuickActions: () => void;
  onContentShow: () => void;
  onStartAutoScroll: () => void;
}

export const useVideoPlayer = ({
  audioRef,
  onShowQuickActions,
  onContentShow,
  onStartAutoScroll
}: UseVideoPlayerProps): UseVideoPlayerReturn => {
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const loopVideoRef = useRef<HTMLVideoElement>(null);

  // Effect to handle video end and transition
  useEffect(() => {
    if (videoEnded) {
      // Start playing the loop video before showing it
      if (loopVideoRef.current) {
        // Ensure loop video is properly configured for mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        loopVideoRef.current.muted = true;
        loopVideoRef.current.playsInline = true;
        loopVideoRef.current.setAttribute('playsinline', 'true');
        loopVideoRef.current.setAttribute('webkit-playsinline', 'true');
        
        if (isMobile) {
          loopVideoRef.current.setAttribute('x5-playsinline', 'true');
          loopVideoRef.current.setAttribute('x5-video-player-type', 'h5-page');
          loopVideoRef.current.style.display = 'block !important';
          loopVideoRef.current.style.visibility = 'visible !important';
          loopVideoRef.current.style.opacity = '1 !important';
        }
        
        // Remove controls from loop video too
        loopVideoRef.current.removeAttribute('controls');
        loopVideoRef.current.controls = false;
        loopVideoRef.current.style.pointerEvents = 'none';
        loopVideoRef.current.style.userSelect = 'none';
        
        loopVideoRef.current.play().catch(() => {
          // Silently handle loop video play errors
        });
      }
      // Show content immediately after loop video starts
      setTimeout(() => {
        setShowContent(true);
        onContentShow();
      }, 100);

      // Start auto-scrolling 5 seconds after content is shown
      setTimeout(() => {
        onStartAutoScroll();
      }, 500);
    }
  }, [videoEnded, onContentShow, onStartAutoScroll]);

  // Initialize video on mount
  useEffect(() => {
    // Preload video for better mobile experience
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, []);

  // Add mobile-specific video handling
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isProduction = window.location.protocol === 'https:' && !window.location.hostname.includes('localhost');
    
    const currentVideo = videoRef.current;
    if (currentVideo) {
      // Configure video for mobile and production
      currentVideo.muted = true;
      currentVideo.playsInline = true;
      currentVideo.setAttribute('playsinline', 'true');
      currentVideo.setAttribute('webkit-playsinline', 'true');
      currentVideo.setAttribute('controls', 'false');
      currentVideo.setAttribute('disablepictureinpicture', 'true');
      currentVideo.setAttribute('x-webkit-airplay', 'deny');
      currentVideo.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      currentVideo.setAttribute('oncontextmenu', 'return false;');
      // Force remove controls attribute completely
      currentVideo.removeAttribute('controls');
      currentVideo.controls = false;
      // Additional control prevention
      currentVideo.style.outline = 'none';
      currentVideo.style.border = 'none';
      
      // ULTRA-AGGRESSIVE control removal - but delay start to prevent flickering
      let removeControlsInterval: NodeJS.Timeout;
      
      // Delay the aggressive control removal to prevent interfering with video initialization
      setTimeout(() => {
        removeControlsInterval = setInterval(() => {
          if (currentVideo) {
            currentVideo.removeAttribute('controls');
            currentVideo.controls = false;
            currentVideo.style.pointerEvents = 'none';
            currentVideo.style.userSelect = 'none';
            
            // Hide any shadow DOM controls that might appear
            const shadowRoot = (currentVideo as any).shadowRoot;
            if (shadowRoot) {
              const controls = shadowRoot.querySelectorAll('[class*="control"], [class*="button"], [class*="timeline"]');
              controls.forEach((control: any) => {
                control.style.display = 'none !important';
                control.style.visibility = 'hidden !important';
                control.style.opacity = '0 !important';
              });
            }
          }
        }, 100); // Reduced frequency and delayed start
      }, 500); // Wait 500ms before starting aggressive control removal
      
      // MutationObserver to watch for any control elements being added
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && currentVideo) {
            // Remove any control-related elements that might be added
            const controlElements = currentVideo.querySelectorAll('[class*="control"], [class*="media"], [controls]');
            controlElements.forEach((el) => {
              el.remove();
            });
          }
        });
      });
      
      observer.observe(currentVideo, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['controls']
      });
      
      // Re-add the mobile and production configuration that was removed
      if (isMobile) {
        currentVideo.setAttribute('x5-playsinline', 'true');
        currentVideo.setAttribute('x5-video-player-type', 'h5-page');
        currentVideo.setAttribute('x5-video-player-fullscreen', 'true');
        currentVideo.setAttribute('x5-video-orientation', 'portraint');
        currentVideo.defaultMuted = true;
        // Force video to be visible on mobile
        currentVideo.style.display = 'block !important';
        currentVideo.style.visibility = 'visible !important';
        currentVideo.style.opacity = '1 !important';
      }
      
      // Enhanced preloading for production to prevent flickering
      if (isProduction) {
        currentVideo.preload = 'auto';
        currentVideo.load();
      }
      
      // Try to preload the video
      const handleCanPlayThrough = () => {
        setVideoLoaded(true);
      };
      
      const handleLoadedData = () => {
        setVideoLoaded(true);
        setVideoError(false);
      };
      
      currentVideo.addEventListener('canplaythrough', handleCanPlayThrough);
      currentVideo.addEventListener('loadeddata', handleLoadedData);
      
      // Clean up interval and observer when component unmounts
      return () => {
        clearInterval(removeControlsInterval);
        observer.disconnect();
        if (currentVideo) {
          currentVideo.removeEventListener('canplaythrough', handleCanPlayThrough);
          currentVideo.removeEventListener('loadeddata', handleLoadedData);
        }
      };
    }
  }, []);

  const handleOpenInvitation = async () => {
    setVideoError(false);
    
    // Show QuickActions after 5 seconds instead of 11.5 seconds
    setTimeout(() => {
      onShowQuickActions();
    }, 5000);
    
    // Detect production environment
    const isProduction = window.location.protocol === 'https:' && !window.location.hostname.includes('localhost');
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    
    try {
      // Start audio first (user interaction allows this)
      if (audioRef.current) {
        try {
          // Force reload in production for better reliability
          if (isProduction) {
            audioRef.current.load();
          }
          await audioRef.current.play();
        } catch (error) {
          // Silently handle audio autoplay failure
        }
      }
      
      // Handle video - this MUST work since user just clicked
      if (videoRef.current) {
        // Production-specific video loading to prevent flickering
        if (isProduction) {
          
          // Pre-configure video for smooth start
          videoRef.current.style.opacity = '0';
          videoRef.current.style.transition = 'opacity 0.3s ease-in-out';
          
          // Always reload video in production for reliability
          videoRef.current.load();
          
          // Wait for video to be ready with better timing
          await new Promise((resolve) => {
            const timeout = setTimeout(resolve, 3000); // Increased timeout for production
            
            const onCanPlayThrough = () => {
              clearTimeout(timeout);
              videoRef.current?.removeEventListener('canplaythrough', onCanPlayThrough);
              // Fade in the video once it's ready
              if (videoRef.current) {
                videoRef.current.style.opacity = '1';
              }
              resolve(true);
            };
            
            if (videoRef.current && videoRef.current.readyState >= 4) {
              clearTimeout(timeout);
              if (videoRef.current) {
                videoRef.current.style.opacity = '1';
              }
              resolve(true);
            } else {
              videoRef.current?.addEventListener('canplaythrough', onCanPlayThrough);
            }
          });
        } else if (!videoLoaded) {
          videoRef.current.load();
        }
        
        // Force all mobile compatibility settings
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('webkit-playsinline', 'true');
        videoRef.current.setAttribute('x5-playsinline', 'true');
        videoRef.current.setAttribute('x5-video-player-type', 'h5-page');
        videoRef.current.setAttribute('x5-video-player-fullscreen', 'true');
        videoRef.current.setAttribute('x5-video-orientation', 'portraint');
        videoRef.current.defaultMuted = true;
        
        // Additional mobile visibility fixes
        if (isMobile && videoRef.current) {
          videoRef.current.style.display = 'block !important';
          videoRef.current.style.visibility = 'visible !important';
          videoRef.current.style.opacity = '1 !important';
          videoRef.current.style.zIndex = '1';
          videoRef.current.style.position = 'absolute';
          videoRef.current.style.top = '0';
          videoRef.current.style.left = '0';
          videoRef.current.style.width = '100%';
          videoRef.current.style.height = '100%';
          videoRef.current.style.objectFit = 'cover';
          videoRef.current.style.backgroundColor = '#000000';
          videoRef.current.style.pointerEvents = 'none';
          videoRef.current.style.userSelect = 'none';
          (videoRef.current.style as any).webkitUserSelect = 'none';
          (videoRef.current.style as any).webkitTouchCallout = 'none';
        }
        
        // Multiple play attempts for production
        let playSuccess = false;
        const maxAttempts = isProduction ? 3 : 1;
        
        for (let attempt = 1; attempt <= maxAttempts && !playSuccess; attempt++) {
          try {
            await videoRef.current.play();
            playSuccess = true;
            
          } catch (error) {
            
            if (attempt < maxAttempts) {
              // Wait before next attempt
              await new Promise(resolve => setTimeout(resolve, 500));
              
              // Try reloading video before next attempt
              videoRef.current.load();
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }
        
        // If all attempts failed, show content
        if (!playSuccess) {
          setVideoError(true);
          setTimeout(() => {
            setVideoEnded(true);
          }, 500);
        }
      }
    } catch (error) {
      // Fallback: show content immediately
      setTimeout(() => {
        setVideoEnded(true);
      }, 500);
    }
  };

  return {
    videoEnded,
    videoError,
    videoLoaded,
    showContent,
    videoRef: videoRef as React.RefObject<HTMLVideoElement>,
    loopVideoRef: loopVideoRef as React.RefObject<HTMLVideoElement>,
    handleOpenInvitation,
    setShowQuickActions: () => {}, // Placeholder - will be handled by parent
    setIsPlaying: () => {} // Placeholder - will be handled by parent
  };
};
